import { useEffect, useRef, useState } from 'react'
import { api, auth } from '../lib/api.js'
import { alertBox, confirmBox } from './Dialog.jsx'
import { outcomeLabel } from '../lib/outcomes.js'
import { paymentLabel } from '../lib/orderPayment.js'

export function OrderPaymentStatus({ conv }) {
  const payment = conv.order_payment
  if (!payment) return null
  return <span className="row" style={{ flexWrap: 'wrap', gap: 8 }} role="status">
    <span>{paymentLabel(conv)}</span>
    {payment.amount != null && <span>实付 {(payment.amount / 100).toFixed(2)} 元</span>}
    {payment.date && <span>支付日期 {payment.date}</span>}
  </span>
}

const STAGES = {
  unbound: '待识别订单', pending: '查询归属中', ready: '已同步', bound: '已绑定',
  verifying: '支付核验中', retrying: '同步重试中', needs_review: '待核实',
  remark_pending: '备注同步中', owner_pending: '归属同步中',
}

export function LegacyOrderStatus({ conv }) {
  const state = conv.order_lookup?.legacyAlignment
  if (!state || state.state === 'excluded') return null
  const labels = { paid: '已付款', unpaid: '未付款', refunded: '支付状态已失效' }
  return (
    <div style={{ margin: '4px 16px', padding: '8px 0', borderBottom: '1px solid var(--line)',
      fontSize: 13, overflowWrap: 'anywhere', flexShrink: 0 }}>
      {state.state === 'needs_review' ? <span role="status">后台待核实：{state.error}</span> : (
        <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span>车网通：{labels[state.paymentState] || '状态待核实'}</span>
          <span>订单 {state.orderId}</span>
          {state.paymentDate && <span>支付日期 {state.paymentDate}</span>}
          {state.ownerResolution === 'departed_fallback' && <span>{conv.owner_name || '离职'}归属</span>}
          {state.warnings?.length > 0 && <span className="muted">历史记录待核实</span>}
        </div>
      )}
    </div>
  )
}

export function OrderSyncStatus({ conv, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const notified = useRef('')
  useEffect(() => {
    const key = `${conv.id}:${conv.order_sync_error || ''}`
    if (conv.order_sync_error && !conv.order_identity_only && notified.current !== key) {
      notified.current = key
      alertBox(conv.order_sync_error)
    }
  }, [conv.id, conv.order_sync_error, conv.order_identity_only])
  async function run(fn) {
    setBusy(true); setErr('')
    try { await fn(conv.id); await onChanged() } catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }
  async function cancel() {
    if (!(await confirmBox('该核验已经开始后台写入。撤回前会再次核查车网通支付、归属和备注，确认未写入后才会撤回。继续吗？'))) return
    await run(api.cancelOrderVerification)
  }
  async function voidIt() {
    if (!(await confirmBox('作废这次核验：本次核验不新增成交，也不修改车网通现有的支付状态和备注。适用于撤回被拒（支付状态或备注已变化）或同步重试中、多次失败的情况，之后可重新标记。继续吗？'))) return
    await run(api.voidOrderVerification)
  }
  return (
    <div style={{ margin: '4px 16px', padding: '8px 0', borderBottom: '1px solid var(--line)', fontSize: 13, overflowWrap: 'anywhere', flexShrink: 0 }}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <span className="muted">{conv.order_identity_only ? '已关联 · 历史待核对'
          : conv.order_sync_stage === 'ready' && conv.owner_resolution === 'departed_fallback'
          ? `${conv.owner_name || '离职'}归属` : STAGES[conv.order_sync_stage] || '待核实'}</span>
        {conv.order_sync_stage !== 'ready' && conv.owner_resolution === 'departed_fallback' &&
          <span className="muted">{conv.owner_name || '离职'}归属</span>}
        {conv.order_id && <span>订单 {conv.order_id}</span>}
        <OrderPaymentStatus conv={conv} />
        {conv.pending_outcome && <span style={{ background: '#e5e7eb', color: '#4b5563', borderRadius: 4, padding: '3px 8px' }}>
          {outcomeLabel(conv.pending_outcome)} · 待核验
        </span>}
        {conv.order_sync_error && !conv.order_identity_only && <button className="btn btn-sm" disabled={busy} onClick={() => run(api.retryOrderSync)}>重试同步</button>}
        {conv.pending_outcome && !conv.order_writes_started && <button className="btn btn-sm" disabled={busy} onClick={() => run(api.cancelOrderVerification)}>撤回核验</button>}
        {auth.isAdmin && conv.pending_outcome && conv.order_writes_started && conv.order_sync_stage === 'needs_review' &&
          <button className="btn btn-sm" disabled={busy} onClick={cancel}>撤回核验</button>}
        {auth.isAdmin && conv.pending_outcome && conv.order_writes_started && ['needs_review', 'retrying'].includes(conv.order_sync_stage) &&
          <button className="btn btn-sm" disabled={busy} onClick={voidIt}>作废核验</button>}
        {!conv.pending_outcome && <button className="btn btn-sm" onClick={() => setEditing(true)}>核对车牌/订单</button>}
      </div>
      {(err || conv.order_sync_error) && <div role="alert" className="err">{err || conv.order_sync_error}</div>}
      {editing && <IdentityModal conv={conv} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await onChanged() }} />}
    </div>
  )
}

function IdentityModal({ conv, onClose, onSaved }) {
  const identity = conv.order_lookup?.identity || {}
  const [plate, setPlate] = useState(identity.plate || '')
  const [phone, setPhone] = useState(identity.phone || '')
  const [orderId, setOrderId] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  async function save(e) {
    e.preventDefault(); setBusy(true); setErr('')
    try { await api.resolveOrderIdentity({ id: conv.id, plate, phone, orderId }); await onSaved() }
    catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <div className="modal-bg">
      <form className="modal" onSubmit={save}>
        <h2>核实本次订单</h2>
        <label className="field">车牌<input className="input" value={plate} onChange={e => setPlate(e.target.value)} autoFocus /></label>
        <label className="field">手机号<input className="input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} /></label>
        <label className="field">订单ID 或 订单编号（选填；填了就只按它绑定，车牌和手机号不参与匹配）<input className="input" value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="车网通订单列表里的 ID 或订单编号" /></label>
        {err && <div role="alert" className="err">{err}</div>}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>取消</button>
          <button className="btn btn-primary" disabled={busy || (!plate.trim() && !phone.trim() && !orderId.trim())}>查询并绑定</button>
        </div>
      </form>
    </div>
  )
}

export function PaymentCheckModal({ outcome, payment, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  async function save(e) {
    e.preventDefault(); setBusy(true); setErr('')
    try { await onConfirm() }
    catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <div className="modal-bg">
      <form className="modal" style={{ maxWidth: 360 }} onSubmit={save}>
        <h2>{outcomeLabel(outcome)}</h2>
        <div className="field"><label>{payment?.historical ? '历史支付日期' : '成交日期'}</label>
          <div className="muted">{payment?.date || '等待支付核实'}</div></div>
        {payment?.historical && <div className="field">历史付款，不计本次成交，原归属不变。</div>}
        {err && <div className="err" role="alert">{err}</div>}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>取消</button>
          <button className="btn btn-primary" disabled={busy}>提交核验</button>
        </div>
      </form>
    </div>
  )
}
